import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { User, Building2 } from "lucide-react";

const R = {
  en: { wrongTab: "This account belongs to the other tab (Player / Club).", player: "Player", club: "Club", clubName: "Club name", city: "City", contact: "Contact name", clubHint: "Create teams, share a join code, follow your players' readiness & injuries.", playerHint: "Your AI twin, coach, medical map and daily log.", forgot: "Forgot password?", verifySent: "Account created — we sent you an email to confirm your address." },
  fr: { wrongTab: "Ce compte appartient à l'autre onglet (Joueur / Club).", player: "Joueur", club: "Club", clubName: "Nom du club", city: "Ville", contact: "Nom du contact", clubHint: "Crée des équipes, partage un code, suis la fraîcheur et les blessures de tes joueurs.", playerHint: "Ton jumeau IA, ton coach, ta carte médicale et ton suivi quotidien.", forgot: "Mot de passe oublié ?", verifySent: "Compte créé — on t'a envoyé un email pour confirmer ton adresse." },
};

const fmtErr = (d) => (Array.isArray(d) ? d.map((e) => e.msg).join(" ") : d);

export default function Auth() {
  const [sp] = useSearchParams();
  const initMode = sp.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initMode);
  const [role, setRole] = useState(sp.get("role") === "club" ? "club" : "player");
  const [form, setForm] = useState({ email: "", password: "", full_name: "", position: "Striker", club_name: "", city: "" });
  const [loading, setLoading] = useState(false);
  const { login, register } = useAuth();
  const { t, lang } = useLang();
  const r = R[lang] || R.en;
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = mode === "login" ? await login(form.email, form.password, role) : await register({ ...form, role });
      toast.success(mode === "register" ? r.verifySent : "OK");
      nav(mode === "register" ? "/pricing" : user.role === "club" ? "/club" : "/dashboard");
    } catch (err) {
      if (err?.response?.status === 403) toast.error(r.wrongTab);
      else toast.error(fmtErr(err?.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "mt-1 bg-[#090C10] border-[#1E2638] text-white";

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-10" data-testid="auth-page">
      <div className="w-full max-w-md rounded-2xl border border-[#1E2638] bg-[#121721] p-8">
        <div className="mb-5">
          <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">DLSPORT · ACCESS</div>
          <h1 className="font-display font-black uppercase text-3xl mt-2" data-testid="auth-title">
            {mode === "login" ? t.auth.loginTitle : t.auth.registerTitle}
          </h1>
        </div>

        <div className="grid grid-cols-2 gap-1 rounded-lg border border-[#1E2638] bg-[#090C10] p-1 mb-5" data-testid="auth-role-tabs">
          {[["player", r.player, User], ["club", r.club, Building2]].map(([id, lbl, Icon]) => (
            <button key={id} type="button" onClick={() => setRole(id)} data-testid={`auth-role-${id}`}
              className={`flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-colors ${role === id ? "bg-[#00F076] text-black" : "text-slate-400 hover:text-white"}`}>
              <Icon className="w-4 h-4" />{lbl}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-400 mb-4" data-testid="auth-role-hint">{role === "club" ? r.clubHint : r.playerHint}</p>

        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && role === "club" && (
            <>
              <div>
                <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.clubName}</Label>
                <Input required value={form.club_name} onChange={(e) => setForm({ ...form, club_name: e.target.value })} data-testid="auth-club-name-input" className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.city}</Label>
                  <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="auth-city-input" className={inputCls} />
                </div>
                <div>
                  <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{r.contact}</Label>
                  <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="auth-name-input" className={inputCls} />
                </div>
              </div>
            </>
          )}
          {mode === "register" && role === "player" && (
            <>
              <div>
                <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.auth.name}</Label>
                <Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="auth-name-input" className={inputCls} />
              </div>
              <div>
                <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.auth.position}</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} data-testid="auth-position-input" className={inputCls} />
              </div>
            </>
          )}
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.auth.email}</Label>
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="auth-email-input" className={inputCls} />
          </div>
          <div>
            <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-400">{t.auth.password}</Label>
            <Input required type="password" minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="auth-password-input" className={inputCls} />
            {mode === "login" && (
              <div className="mt-2 text-right">
                <Link to="/forgot-password" className="text-xs text-slate-400 hover:text-[#00F076]" data-testid="auth-forgot-link">{r.forgot}</Link>
              </div>
            )}
          </div>
          <Button type="submit" disabled={loading} data-testid="auth-submit" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase tracking-wider">
            {loading ? "…" : mode === "login" ? t.auth.submitLogin : t.auth.submitRegister}
          </Button>
        </form>

        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-6 w-full text-center text-sm text-slate-400 hover:text-white" data-testid="auth-switch-mode">
          {mode === "login" ? t.auth.toRegister : t.auth.toLogin}
        </button>
        <div className="mt-4 text-center">
          <Link to="/" className="text-xs font-mono-tech tracking-wider text-slate-500 hover:text-slate-300">← HOME</Link>
        </div>
      </div>
    </div>
  );
}
